import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';

import { DeviceUiChannelsDeviceBehaviorPlugin } from '@shure/device-ui/channels/data-access';
import { IntellimixType } from '@shure/device-ui/intellimix/data-access';
import { DeviceUiIntellimixDeviceBehaviorPlugin } from '@shure/device-ui/intellimix/data-access';
import { FactoryResetText } from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsFactoryResetBehaviorPlugin } from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsGeneralBehaviorPlugin } from '@shure/device-ui/settings/general/data-access';
import { DspType } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import {
	DeviceMixerType,
	DeviceModel,
	LobeStyle,
	Placement
} from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import {
	CoveragePerspectives,
	DeviceCoverageDeviceBehaviorPlugin,
	DevicePerspective,
	PERSPECTIVE_MAP
} from '@shure/shared/angular/features/device-coverage/data-access';
import { CoverageImage, CoverageImageProvider } from '@shure/shared/angular/features/device-coverage/ui';
import {
	LightsSettingsDeviceBehaviorPlugin,
	LightsSettingsSupportType
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import {
	LightsImage,
	LightsImageProvider
} from '@shure/shared/angular/features/device-lights-settings/feature-device-image-next';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class Mxa710DeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSettingsFactoryResetBehaviorPlugin,
		LightsSettingsDeviceBehaviorPlugin,
		DeviceUiIntellimixDeviceBehaviorPlugin,
		DeviceUiChannelsDeviceBehaviorPlugin,
		DeviceUiSettingsGeneralBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		DeviceCoverageDeviceBehaviorPlugin,
		CoverageImageProvider,
		LightsImageProvider
{
	public readonly models: DeviceModel[] = [DeviceModel.MXA7102FT, DeviceModel.MXA7104FT];

	constructor(protected readonly transloco: TranslocoService) {}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		const pages = [
			{
				route: DeviceUiRoute.Channels,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.channels')
			},
			{
				route: DeviceUiRoute.Intellimix,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.intellimix')
			},
			{
				route: DeviceUiRoute.Presets,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.presets')
			},
			{
				route: DeviceUiRoute.Settings,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.settings')
			}
		];

		return of(pages);
	}

	public getDefaultRoute(_model: DeviceModel): DeviceUiRoute | undefined {
		return DeviceUiRoute.Intellimix;
	}

	public getResetText(_model: DeviceModel): FactoryResetText {
		return {
			tab$: this.transloco.selectTranslate('scDevUiSharedDevBehavior.settings.factory-reset'),
			title: this.transloco.translate('scDevUiSharedDevBehavior.settings.factory-reset'),
			button: this.transloco.translate(
				'scDevUiSharedDevBehavior.settings.factory-reset-page.restore-factory-defaults'
			)
		};
	}

	// FIXME DJC need to pass placement map as an optional parameter into the device-image-device-behavior-service
	protected readonly DEVICE_PERSPECTIVE_IMAGE_MAP: Record<DevicePerspective, string> = {
		[DevicePerspective.Bottom]: 'bottom',
		[DevicePerspective.Top]: 'top',
		[DevicePerspective.Side]: 'side',
		[DevicePerspective.End]: 'front'
	};

	public getCoverageImage(
		model: DeviceModel,
		perspective: CoveragePerspectives,
		placement: Placement
	): CoverageImage {
		const devicePerspective = PERSPECTIVE_MAP[perspective][placement];
		const imageParam = this.DEVICE_PERSPECTIVE_IMAGE_MAP[devicePerspective];

		return {
			live: `device-${model.toLowerCase()}-${imageParam}.svg`,
			virtual: `device-${model.toLowerCase()}-${imageParam}-line.svg`,
			isImageRound: false
		};
	}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.Supported;
	}

	public getLightsImage(): LightsImage {
		return {
			imageUrl: 'device-mxa7xx.svg'
		};
	}

	public showPatentInfo(): boolean {
		return true;
	}

	public getIntellimixType(_model: DeviceModel): IntellimixType | undefined {
		return IntellimixType.Advanced;
	}

	public revertAutomixerDefaultsCapability(_model: DeviceModel): boolean {
		return true;
	}

	public getMixerType(_model: DeviceModel): DeviceMixerType | undefined {
		return DeviceMixerType.Intellimix;
	}

	public getIntellimixChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> | undefined {
		return new Map<DspType, number>([
			[DspType.Aec, 1],
			[DspType.Nr, 2],
			[DspType.Peq, 3],
			[DspType.Compressor, 4],
			[DspType.Delay, 5]
		]);
	}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}

	public getChannelsChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> | undefined {
		return new Map<DspType, number>([
			[DspType.Peq, 1],
			[DspType.Eq, 2]
		]);
	}

	public usePostGateValues(): boolean {
		return true;
	}

	public getPlacementLobeStyle(perspective: CoveragePerspectives, placement: Placement): LobeStyle {
		if (perspective === CoveragePerspectives.Side) {
			return LobeStyle.Wedge;
		}
		switch (placement) {
			case Placement.WallVertical:
				return LobeStyle.Stacked;
			case Placement.Ceiling:
				return LobeStyle.Ellipse;
			default:
				return LobeStyle.Wedge;
		}
	}

	public getPlacementHasComplimentaryLobes(placement: Placement): boolean {
		switch (placement) {
			case Placement.WallHorizontal:
			case Placement.WallVertical:
				return false;
			case Placement.Ceiling:
			case Placement.Table:
				return true;
			default:
				return false;
		}
	}

	public hasSideView(placement: Placement): boolean {
		return placement === Placement.WallVertical;
	}

	public supportsChannels(): boolean {
		return true;
	}
}
